// Make a number look alive when it moves.
//
// Owner, 2026-10-05: "how can we make the live indexes and today's live price
// look alive on the home page and secondary menu??? … They need to update the
// minute new lots come in and flash showing the change."
//
// ⚠️ GREEN UP, RED DOWN — the owner's standing rule, and the same direction
// the rail, the strip and the index pages already use for the day change. A
// flash that used red for "changed" would read as a price drop.
//
// ⚠️ IT MUST NOT FLASH ON FIRST PAINT. The first value a component sees is not
// a change, it is the number arriving. Flashing every tile gold the moment the
// page hydrates is the thing that makes a page look broken rather than live,
// so the first observed value is recorded silently.

import { useEffect, useRef, useState } from "react";

export type FlashDir = "up" | "down" | null;

/** How long the tint lingers. Long enough to catch out of the corner of an
 *  eye, short enough that a busy index is not permanently lit. */
const FLASH_MS = 1100;

/**
 * Watch a number and report which way it just moved.
 *
 * Pass the raw numeric value, not the formatted string: "$342.30" and
 * "$342.3" are different strings and the same price, and comparing the text
 * would flash on a formatting change.
 */
export function useFlash(value: number | null | undefined): FlashDir {
  const [dir, setDir] = useState<FlashDir>(null);
  const prev = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    const before = prev.current;
    prev.current = value;

    // First value seen — record it, do not flash.
    if (before === undefined) return;
    if (value == null || before == null || value === before) return;

    setDir(value > before ? "up" : "down");
    const id = window.setTimeout(() => setDir(null), FLASH_MS);
    return () => window.clearTimeout(id);
  }, [value]);

  return dir;
}
